/**
 * `rekor-base-change` queue body — the live-update change signal.
 *
 * **Direction: Rekor produces, WayAI consumes.** BaseDO emits ids at its sync-flush seam;
 * WayAI's consumer maps them into a `base_changed` live-update frame and fans them out.
 * WayAI stays the sole writer of `LiveUpdateSession` — Rekor never touches WayAI's
 * per-user session type — and the browser keeps ONE WebSocket.
 *
 * A queue rather than a service binding because a Rekor→WayAI binding would be a reverse
 * dependency and a binding cycle against the preserved deploy-ordering rule — the same
 * reasoning that applies to the entitlement nudge.
 *
 * Lives beside `rekor-attestation.ts` rather than in `live-updates.ts`: that file holds
 * WayAI's OWN live-update API surface (nav, typing, WS frames), while this is a cross-repo
 * wire contract that a separate repo's producer must match.
 */

import { z } from 'zod';

/**
 * **Ids only, ever.** No record content, no names, no counts — content crosses only the
 * authenticated read path (browser → WayAI `/api/data/*` proxy → binding), so a live-update
 * frame can never become an end-run around authorization. That is this seam's own pin,
 * deliberately stricter than AGENTS.md's queue row, which permits "identity or ciphertext".
 *
 * **Lossy by design.** A dropped signal costs staleness-until-navigation, never correctness
 * — the UI's source of truth is the read path. A duplicate costs one redundant refetch,
 * absorbed by the consumer's batch dedupe and the client's coalescing. This is the OPPOSITE
 * posture from the entitlement nudge, where a lost nudge is unrecovered staleness inside an enforcement gate:
 * this seam takes no retry pressure and must not be "hardened" into a data channel.
 *
 * Deliberately NOT `.strict()`: the producer is a separate repo on its own deploy cadence,
 * and strict would couple cross-repo deploy order for no added property — an added field
 * would dead-letter every signal instead of being dropped. What makes that safe is the
 * consumer forwarding the parse OUTPUT and never the raw body: Zod strips unknown keys, so
 * a field smuggled in by a producer-side bug is gone before the frame is built. Both halves
 * are required; the parse alone is only one.
 */
export const rekorBaseChangeMessageSchema = z.object({
  /** The WayAI org UUID — one id namespace. */
  org_id: z.string().uuid(),
  /**
   * Not a UUID: a preview base id is `{prod_base_id}--{slug}`. Immutable per base — a
   * preview clone is its OWN base_id — so `(base_id, scope)` keys never collide across
   * environments. Empty is never a real base id, so it is non-conforming rather than a
   * frame keyed on nothing.
   */
  base_id: z.string().min(1),
  /**
   * Rides along so the client scopes its refetch without a lookup; the inspector scopes
   * by side.
   */
  environment: z.enum(['production', 'preview']),
  /** Which pane refetches. `base` covers create/delete/promote/config changes. */
  scope: z.enum(['records', 'record_types', 'files', 'base']),
});

export type RekorBaseChangeMessage = z.infer<typeof rekorBaseChangeMessageSchema>;
